import React, { useState, useCallback } from 'react';
import {
  makeStyles,
  Typography,
  Box,
  TextField,
  MenuItem,
} from '@material-ui/core';
import {
  ArrowRightOutlined as ArrowRightOutlinedIcon,
  FileCopy as ContentCopyIcon,
} from '@material-ui/icons';
import { useTranslation, Trans } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';

import CardWithProgressLoading from '../CardWithLoadingProgress';
import ButtonWithCirculaProgress from '../ButtonWithCirculaProgress';
import { useRequest, useCopyToClipboard, useLazyRequest } from '../../hooks';
import {
  getCurrenciesNetworksConfig,
  createPaymentLink,
} from '../../../services/merchantsService';
import { getHelperText, hasError } from '../../../utils';

import { FIELDS_NAME, initialValues, validationSchema } from './utils';
import styles from './styles';

const useStyles = makeStyles(styles);

const StoreQrWidget = () => {
  const { t } = useTranslation('common');
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [currencies, loadingGetCurrencies] = useRequest(
    {
      initialState: [],
      request: getCurrenciesNetworksConfig,
      payload: { v2: true },
      transformResponse: (response) =>
        response.data.currencies.filter((currency) => currency.allow),
    },
    [],
  );

  const [, copyToClipboard] = useCopyToClipboard(
    t('payments.digitalQR.copyPaymentLink.successMessage'),
  );

  const [loadingCreatePaymentLink, setLoadingCreatePaymentLink] = useState(
    false,
  );

  const [, , , requestCreatePaymentLink] = useLazyRequest({
    request: createPaymentLink,
    withPostSuccess: (response) => {
      copyToClipboard(response.data.url);
    },
    withPostFailure: (response) => {
      enqueueSnackbar(response.errorData.message, { variant: 'error' });
    },
  });

  const executeGeneratePaymentLink = useCallback(
    async (values, { resetForm }) => {
      setLoadingCreatePaymentLink(true);

      await requestCreatePaymentLink({
        currency_id: values[FIELDS_NAME.PAYMENT_CURRENCY],
        amount: values[FIELDS_NAME.AMOUNT],
      });

      setTimeout(() => {
        setLoadingCreatePaymentLink(false);
        resetForm();
      }, 500);
    },
    [requestCreatePaymentLink],
  );

  const {
    touched,
    errors,
    submitForm,
    getFieldProps,
    dirty,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema: validationSchema(t),
    onSubmit: executeGeneratePaymentLink,
  });

  return (
    <CardWithProgressLoading
      loading={loadingGetCurrencies}
      className={classes.card}
      progressSize={65}
      fullHeight={false}
    >
      <Typography variant="h6" className={classes.title}>
        {t('payments.digitalQR.title')}
      </Typography>

      <Box className={classes.content}>
        <Box className={classes.info}>
          <Typography className={classes.text}>
            <Trans
              t={t}
              i18nKey="payments.digitalQR.description1"
              tOptions={{ interpolation: { escapeValue: false } }}
            />
          </Typography>

          <Typography className={classes.text}>
            <Trans
              t={t}
              i18nKey="payments.digitalQR.description2"
              tOptions={{ interpolation: { escapeValue: false } }}
            />
          </Typography>
        </Box>

        <Box className={classes.inputs}>
          <TextField
            fullWidth
            variant="outlined"
            label={t('payments.digitalQR.paymentCurrency')}
            autoComplete="off"
            color="secondary"
            InputLabelProps={{ shrink: true }}
            helperText={getHelperText(
              touched,
              errors,
              FIELDS_NAME.PAYMENT_CURRENCY,
              '',
            )}
            error={hasError(touched, errors, FIELDS_NAME.PAYMENT_CURRENCY)}
            {...getFieldProps(FIELDS_NAME.PAYMENT_CURRENCY)}
            select
            SelectProps={{
              IconComponent: ArrowRightOutlinedIcon,
            }}
          >
            {currencies.map((currency) => (
              <MenuItem key={`currency-${currency.id}`} value={currency.id}>
                {currency.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            variant="outlined"
            type="number"
            label={t('payments.digitalQR.amount')}
            autoComplete="off"
            color="secondary"
            InputLabelProps={{ shrink: true }}
            helperText={getHelperText(touched, errors, FIELDS_NAME.AMOUNT, '')}
            error={hasError(touched, errors, FIELDS_NAME.AMOUNT)}
            {...getFieldProps(FIELDS_NAME.AMOUNT)}
          />
        </Box>

        <ButtonWithCirculaProgress
          variant="contained"
          color="secondary"
          fullWidth
          disabled={!dirty || !isValid}
          loading={loadingCreatePaymentLink}
          label={t(`payments.digitalQR.copyQR`)}
          className={classes.button}
          handleButtonClick={submitForm}
          startIcon={<ContentCopyIcon />}
        />
      </Box>
    </CardWithProgressLoading>
  );
};

export default StoreQrWidget;
